import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState } from '@/components/DataState';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { platformKindLabel } from '@/data/leads-model';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { commitImport, inspectWorkbook, newImportKey, previewImport, readFileBase64 } from '@/data/lead-import';
import {
  DEFAULT_CAMPAIGN, DEFAULT_SOURCE_DIRECTORY, IMPORT_STEPS, buildImportRequest, describeImportSummary, fileProblem,
  initialMapping, mappedCount, mappingProblems, outcomeLabel, pickSheet, reasonBreakdown, reasonText, sheetHeaders, stepIndex,
  type ImportField, type ImportResponse, type ImportStep, type InspectResponse,
} from '@/data/lead-import-model';
import { cn } from '@/lib/utils';

/* /sales/import — owner-only spreadsheet import (Outreach lead import).
 * Upload .xlsx → pick sheet → map columns (saved preset applied automatically
 * for the prospects workbook; every column editable) → PREVIEW (server dry-run:
 * validation + dedup, zero writes) → Import (one idempotency key per decision;
 * retry replays) → Result with imported / merged / skipped / failed and reasons.
 * The file is parsed SERVER-side including cell hyperlink targets. NO SEND. */

const OUTCOME_TONE: Record<string, Parameters<typeof Badge>[0]['tone']> = { imported: 'success', merged: 'compliance', skipped: 'neutral', failed: 'danger' };

export function LeadImportPage() {
  const { activeWorkspace } = useAuth();
  const { active, domains } = useActiveDomain();
  const isOwner = activeWorkspace?.role === 'owner';

  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<{ name: string; b64: string } | null>(null);
  const [inspect, setInspect] = useState<InspectResponse | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, ImportField>>({});
  const [projectId, setProjectId] = useState<number | ''>(active?.projectId ?? '');
  const [campaign, setCampaign] = useState(DEFAULT_CAMPAIGN);
  const [sourceDirectory, setSourceDirectory] = useState(DEFAULT_SOURCE_DIRECTORY);
  const [workbookKey, setWorkbookKey] = useState('');
  const [preview, setPreview] = useState<ImportResponse | null>(null);
  const [result, setResult] = useState<ImportResponse | null>(null);
  const [commitKey, setCommitKey] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');

  const headers = useMemo(() => (inspect ? sheetHeaders(inspect, sheet) : []), [inspect, sheet]);
  const problems = useMemo(() => mappingProblems(mapping), [mapping]);
  const effectiveProject = projectId === '' ? (active?.projectId ?? null) : projectId;
  const projectLabel = domains.find((d) => d.projectId === effectiveProject)?.hostname ?? (effectiveProject ? `project ${effectiveProject}` : 'no domain selected');

  if (!isOwner) {
    return (
      <div>
        <PageHeader title="Import leads" subtitle="Spreadsheet import is owner-only." />
        <EmptyState title="Owner only" teach="Importing prospects writes to the shared lead pool, so only the workspace owner can run it. Ask the owner to import, or use Add lead for one business." />
      </div>
    );
  }

  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? `${e.message}` : fallback);

  const onFile = async (f: File | undefined) => {
    setError(null); setPreview(null); setResult(null);
    if (!f) return;
    const problem = fileProblem(f.name, f.size);
    if (problem) { setError(problem); return; }
    setBusy('Reading workbook…');
    try {
      const b64 = await readFileBase64(f);
      const ins = await inspectWorkbook(f.name, b64);
      setFile({ name: f.name, b64 });
      setInspect(ins);
      const s = pickSheet(ins);
      setSheet(s);
      setMapping(initialMapping(sheetHeaders(ins, s), ins.suggested_mapping));
      setWorkbookKey(ins.preset.applies ? ins.preset.workbook_key : f.name.replace(/\.xlsx$/i, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40));
      setStep('sheet');
    } catch (e) {
      fail(e, 'Could not read that workbook.');
    } finally {
      setBusy(null);
    }
  };

  const chooseSheet = (name: string) => {
    if (!inspect) return;
    setSheet(name);
    setMapping(initialMapping(sheetHeaders(inspect, name), inspect.suggested_mapping));
  };

  const request = (dryRun: boolean) => {
    if (!file || !sheet || !effectiveProject) return null;
    const sh = inspect?.sheets.find((s) => s.name === sheet);
    return buildImportRequest({ fileName: file.name, fileB64: file.b64, sheet, headerRow: sh?.header_row, mapping, projectId: effectiveProject, campaign, sourceDirectory, workbookKey, dryRun });
  };

  const runPreview = async () => {
    const req = request(true);
    if (!req) { setError('Choose the domain these leads belong to first.'); return; }
    setError(null); setBusy('Checking every row…');
    try {
      setPreview(await previewImport(req));
      setCommitKey(newImportKey()); // one key per import decision; a retry reuses it
      setStep('preview');
    } catch (e) { fail(e, 'Preview failed.'); } finally { setBusy(null); }
  };

  const runCommit = async () => {
    const req = request(false);
    if (!req) return;
    setError(null); setBusy('Importing…');
    try {
      setResult(await commitImport(req, commitKey));
      setStep('result');
    } catch (e) { fail(e, 'Import failed — tapping Import again is safe (same key replays, never duplicates).'); } finally { setBusy(null); }
  };

  const reset = () => { setStep('upload'); setFile(null); setInspect(null); setSheet(null); setMapping({}); setPreview(null); setResult(null); setError(null); setFilter(''); };

  const shown = step === 'result' ? result : preview;
  const rows = (shown?.results ?? []).filter((r) => !filter || r.outcome === filter);

  return (
    <div data-testid="lead-import">
      <PageHeader title="Import leads" subtitle={`Upload the prospects workbook (.xlsx). Leads attach to ${projectLabel}.`} actions={<Link to="/sales/leads" className="text-sm text-text-secondary underline">Back to leads</Link>} />

      <Stepper step={step} />

      {error ? <div className="mb-3 rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{error}</div> : null}
      {busy ? <div className="mb-3 rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">{busy}</div> : null}

      {step === 'upload' && (
        <Card className="p-4">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-text-primary">Workbook file</span>
            <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="block w-full text-sm" onChange={(e) => void onFile(e.target.files?.[0])} data-testid="import-file" />
          </label>
          <p className="mt-3 text-xs text-text-tertiary">Excel .xlsx only, up to 3 MB. Link cells (“Open Google Maps”, “View platform evidence”) are read as their real targets. Nothing is saved until you confirm after the preview.</p>
        </Card>
      )}

      {step === 'sheet' && inspect && (
        <Card className="p-4">
          <h2 className="text-sm font-semibold text-text-primary">Which sheet holds the prospects?</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {inspect.sheets.map((s) => (
              <button key={s.name} type="button" onClick={() => chooseSheet(s.name)} className={cn('rounded-lg border p-3 text-left', sheet === s.name ? 'border-engine bg-engine-wash' : 'border-border-subtle bg-surface hover:bg-hover')}>
                <div className="text-sm font-medium text-text-primary">{s.name}{s.name === inspect.suggested_sheet ? <Badge tone="compliance" className="ml-2">suggested</Badge> : null}</div>
                <div className="text-xs text-text-tertiary">{s.data_rows} data rows · {s.headers.length} columns · header on row {s.header_row || '?'}</div>
              </button>
            ))}
          </div>
          {inspect.preset.applies ? <p className="mt-3 text-xs text-success">Recognised: {inspect.preset.label} — the saved column mapping will be applied.</p> : <p className="mt-3 text-xs text-text-tertiary">New layout — columns will be suggested from their names; check them on the next step.</p>}
          <div className="mt-4 flex gap-2">
            <Button variant="primary" className="h-11" onClick={() => setStep('mapping')} disabled={!sheet || !headers.length}>Next: columns</Button>
            <Button variant="ghost" className="h-11" onClick={reset}>Choose another file</Button>
          </div>
        </Card>
      )}

      {step === 'mapping' && inspect && (
        <div className="space-y-3">
          <Card className="p-4">
            <h2 className="text-sm font-semibold text-text-primary">Where do these leads belong?</h2>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <label className="block text-xs text-text-tertiary">Domain (project)
                <select className={inputCls} value={projectId === '' ? (active?.projectId ?? '') : projectId} onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : '')} data-testid="import-project">
                  <option value="">Choose…</option>
                  {domains.map((d) => <option key={d.projectId} value={d.projectId}>{d.hostname}</option>)}
                </select>
              </label>
              <label className="block text-xs text-text-tertiary">Campaign
                <input className={inputCls} value={campaign} onChange={(e) => setCampaign(e.target.value)} maxLength={64} />
              </label>
              <label className="block text-xs text-text-tertiary">Source label
                <input className={inputCls} value={sourceDirectory} onChange={(e) => setSourceDirectory(e.target.value)} maxLength={100} />
              </label>
            </div>
            <label className="mt-2 block text-xs text-text-tertiary">Workbook key (re-upload identity: project + this key + row ID)
              <input className={inputCls} value={workbookKey} onChange={(e) => setWorkbookKey(e.target.value)} maxLength={40} />
            </label>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-text-primary">Columns → fields</h2>
              <span className="text-xs text-text-tertiary">{mappedCount(mapping)} of {headers.length} columns kept</span>
            </div>
            <ul className="mt-3 divide-y divide-border-subtle" data-testid="import-mapping">
              {headers.map((h) => (
                <li key={h.col} className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-2 sm:items-center sm:gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-text-primary">{h.label}{h.has_links ? <Badge tone="compliance" className="ml-2">links</Badge> : null}</div>
                    <div className="truncate text-[11px] text-text-tertiary">{h.samples.join(' · ') || 'no sample values'}</div>
                  </div>
                  <select className={inputCls} value={mapping[h.label] ?? 'ignore'} onChange={(e) => setMapping({ ...mapping, [h.label]: e.target.value as ImportField })} aria-label={`Field for ${h.label}`}>
                    {Object.entries(inspect.fields).map(([k, f]) => <option key={k} value={k}>{f.label}{f.existing || k === 'ignore' ? '' : ' (new)'}</option>)}
                  </select>
                </li>
              ))}
            </ul>
            {problems.length ? <ul className="mt-3 space-y-1 text-xs text-danger">{problems.map((p) => <li key={p}>{p}</li>)}</ul> : null}
            <div className="mt-4 flex gap-2">
              <Button variant="primary" className="h-11" onClick={() => void runPreview()} disabled={problems.length > 0 || !effectiveProject || !!busy}>Preview (no changes yet)</Button>
              <Button variant="ghost" className="h-11" onClick={() => setStep('sheet')}>Back</Button>
            </div>
          </Card>
        </div>
      )}

      {(step === 'preview' || step === 'result') && shown && (
        <div className="space-y-3">
          <Card className="p-4" data-testid="import-summary">
            <h2 className="text-sm font-semibold text-text-primary">{shown.dry_run ? 'Preview — nothing has been saved' : 'Import complete'}</h2>
            <p className="mt-1 text-sm text-text-secondary">{describeImportSummary(shown.summary, shown.dry_run)}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label={outcomeLabel('imported', shown.dry_run)} value={shown.summary.imported} tone="success" onClick={() => setFilter('imported')} active={filter === 'imported'} />
              <Stat label={outcomeLabel('merged', shown.dry_run)} value={shown.summary.merged} tone="compliance" onClick={() => setFilter('merged')} active={filter === 'merged'} />
              <Stat label={outcomeLabel('skipped', shown.dry_run)} value={shown.summary.skipped} tone="neutral" onClick={() => setFilter('skipped')} active={filter === 'skipped'} />
              <Stat label="Failed" value={shown.summary.failed} tone="danger" onClick={() => setFilter('failed')} active={filter === 'failed'} />
            </div>
            {Object.keys(shown.reasons).length ? (
              <ul className="mt-3 space-y-1 text-xs text-text-secondary">
                {reasonBreakdown(shown.reasons).map((r) => <li key={r.code}><span className="font-medium text-text-primary">{r.count}</span> · {r.text}</li>)}
              </ul>
            ) : null}
            {filter ? <button type="button" className="mt-2 text-xs underline" onClick={() => setFilter('')}>Show all rows</button> : null}
            <div className="mt-4 flex flex-wrap gap-2">
              {shown.dry_run ? (
                <>
                  <Button variant="primary" className="h-11" onClick={() => void runCommit()} disabled={!!busy || shown.summary.imported + shown.summary.merged === 0}>
                    Import {shown.summary.imported + shown.summary.merged} leads into {projectLabel}
                  </Button>
                  <Button variant="ghost" className="h-11" onClick={() => setStep('mapping')}>Back to columns</Button>
                </>
              ) : (
                <>
                  <Link to="/sales/leads" className="inline-flex h-11 items-center rounded-md bg-neutral-10 px-3 text-sm font-medium text-neutral-0 dark:bg-neutral-11">Open leads</Link>
                  <Button variant="ghost" className="h-11" onClick={reset}>Import another file</Button>
                </>
              )}
            </div>
            {!shown.dry_run && shown.import_batch_id ? <p className="mt-2 text-[11px] text-text-tertiary">Batch {shown.import_batch_id}. Uploading the same workbook again adds nothing new — rows are matched by workbook row ID, then email, website and name + postcode.</p> : null}
          </Card>

          <Card className="overflow-hidden">
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full min-w-[640px] border-collapse text-sm" data-testid="import-rows">
                <thead className="sticky top-0 bg-inset/80 text-left text-[11px] uppercase tracking-wide text-text-tertiary backdrop-blur">
                  <tr><th className="px-3 py-2">Row</th><th className="px-3 py-2">Business</th><th className="px-3 py-2">Outcome</th><th className="px-3 py-2">Why / notes</th><th className="px-3 py-2">Platform</th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.row} className="border-t border-border-subtle align-top">
                      <td className="px-3 py-2 text-xs text-text-tertiary">{r.row_ref?.split(':').pop() ?? r.row}</td>
                      <td className="px-3 py-2">
                        <div className="text-text-primary">{r.business_name ?? <span className="text-text-tertiary">(no name)</span>}</div>
                        <div className="text-[11px] text-text-tertiary">{[r.preview.city_region, r.preview.postcode].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={OUTCOME_TONE[r.outcome]}>{outcomeLabel(r.outcome, shown.dry_run)}</Badge>
                        {r.matched_by ? <div className="mt-1 text-[11px] text-text-tertiary">matched by {r.matched_by.replace(/_/g, ' ')}{r.lead_ref ? ` · ${r.lead_ref}` : ''}</div> : null}
                      </td>
                      <td className="px-3 py-2 text-xs text-text-secondary">
                        {r.reasons.map((c) => <div key={c}>{reasonText(c)}</div>)}
                        {r.warnings.map((c) => <div key={c} className="text-text-tertiary">{reasonText(c)}</div>)}
                        {r.filled?.length ? <div className="text-text-tertiary">fills: {r.filled.join(', ')}</div> : null}
                      </td>
                      <td className="px-3 py-2 text-xs text-text-secondary">{r.preview.booking_platform ?? '—'}<div className="text-[11px] text-text-tertiary">{r.preview.booking_platform_kind ? platformKindLabel(r.preview.booking_platform_kind) : ''}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

const inputCls = 'mt-1 h-11 w-full rounded-md border border-border-default bg-surface px-3 text-sm text-text-primary';

function Stepper({ step }: { step: ImportStep }) {
  const idx = stepIndex(step);
  return (
    <ol className="mb-4 flex items-center gap-1 text-[11px]" aria-label="Import steps">
      {IMPORT_STEPS.map((s, i) => (
        <li key={s.key} className={cn('flex items-center gap-1', i <= idx ? 'text-text-primary' : 'text-text-tertiary')}>
          <span className={cn('flex h-5 w-5 items-center justify-center rounded-full border text-[10px]', i < idx ? 'border-engine bg-engine text-white' : i === idx ? 'border-engine text-engine' : 'border-border-subtle')}>{i + 1}</span>
          <span className="hidden sm:inline">{s.label}</span>
          {i < IMPORT_STEPS.length - 1 ? <span className="mx-1 h-px w-4 bg-border-subtle" /> : null}
        </li>
      ))}
    </ol>
  );
}

function Stat({ label, value, tone, onClick, active }: { label: string; value: number; tone: Parameters<typeof Badge>[0]['tone']; onClick: () => void; active: boolean }) {
  return (
    <button type="button" onClick={onClick} className={cn('rounded-lg border p-3 text-left', active ? 'border-engine bg-engine-wash' : 'border-border-subtle bg-surface hover:bg-hover')}>
      <div className="text-2xl font-semibold text-text-primary" data-tabular>{value}</div>
      <Badge tone={tone}>{label}</Badge>
    </button>
  );
}
